import { useEffect, useState } from 'react';
import { streamerAccountApi } from '../api';
import './StreamerAccount.css';

export default function StreamerAccount() {
  const [loading, setLoading] = useState(true);
  const [redeeming, setRedeeming] = useState(false);
  const [liveSaving, setLiveSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [account, setAccount] = useState(null);
  const [redeemAmount, setRedeemAmount] = useState('');

  const loadAccount = async () => {
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const data = await streamerAccountApi.getAccount();
      setAccount(data);
    } catch (e) {
      setError(e?.response?.data?.error || 'failed_to_load');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAccount();
  }, []);

  const handleRedeem = async (e) => {
    e.preventDefault();
    const amount = Math.max(0, parseInt(redeemAmount || '0', 10) || 0);
    if (amount <= 0) return;
    setRedeeming(true);
    setError('');
    setSuccess('');
    try {
      const result = await streamerAccountApi.redeemCoins(amount);
      setSuccess(`Erfolgreich ${result.redeemed_coins || amount} Coins eingelöst.`);
      setRedeemAmount('');
      await loadAccount();
    } catch (e) {
      setError(e?.response?.data?.error || 'redeem_failed');
    } finally {
      setRedeeming(false);
    }
  };

  const handleLiveToggle = async () => {
    const nextValue = !account?.manual_live;
    setLiveSaving(true);
    setError('');
    setSuccess('');
    try {
      const result = await streamerAccountApi.setLive(nextValue);
      setAccount((prev) => ({ ...prev, manual_live: result.manual_live }));
      setSuccess(
        result.manual_live
          ? 'Du bist jetzt als Live markiert.'
          : 'Live-Markierung entfernt.'
      );
    } catch (e) {
      setError(e?.response?.data?.error || 'live_toggle_failed');
    } finally {
      setLiveSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="page streamer-account-page">
        <div className="loading-container">
          <div className="spinner" />
          <p className="muted">Lädt...</p>
        </div>
      </div>
    );
  }

  if (!account?.linked) {
    return (
      <div className="page streamer-account-page">
        <h1>Dein Account</h1>
        <p className="streamer-account-subtitle">Streamer Account</p>
        <div className="streamer-account-card">
          <p className="muted">Du bist kein eingetragender Streamer. Bitte eröffne ein Ticket auf unserem Discord.</p>
        </div>
      </div>
    );
  }

  const wallet = account.wallet || { coins: 0, last_hourly_reward: null };
  const manualLive = !!account.manual_live;
  const platformLive =
    account.streamer?.status === 'live' || account.streamer?.tiktok_status === 'live';

  return (
    <div className="page streamer-account-page">
      <h1>Dein Account</h1>
      <p className="streamer-account-subtitle">Deine Account-Informationen und Coin-Bilanz</p>

      {account.auto && (
        <p className="streamer-account-auto">Dein Account wurde automatisch anhand deiner Discord-ID zugeordnet.</p>
      )}

      {!account.creator_plus && (
        <div className="streamer-account-card streamer-account-card-restricted">
          <p>
            Du hast keine Creator+ Rolle. Daher bekommst du keine Coins pro Stunde und kannst Features wie den Chat nicht nutzen.
          </p>
        </div>
      )}

      <div className="streamer-account-card">
        <div className="streamer-account-header">
          <div className="streamer-account-avatar">
            {account.streamer?.display_name?.[0] || account.streamer?.username?.[0] || '?'}
          </div>
          <div>
            <h2>{account.streamer?.display_name || account.streamer?.username || 'Unbekannt'}</h2>
            <p className="muted">@{account.streamer?.username || ''} • {account.streamer?.platform || ''}</p>
          </div>
        </div>

        <div className="streamer-account-coins">
          <div className="streamer-account-coins-value">{wallet.coins}</div>
          <div className="streamer-account-coins-label">Coins</div>
        </div>

        <div className="streamer-account-meta">
          <div className="streamer-account-meta-item">
            <span className="muted">Letzte Belohnung</span>
            <span>{wallet.last_hourly_reward ? new Date(wallet.last_hourly_reward).toLocaleString('de-DE') : 'Noch keine'}</span>
          </div>
          <div className="streamer-account-meta-item">
            <span className="muted">Verdienst pro Stunde</span>
            <span>{account.creator_plus ? '5 Coins' : 'Keine Coins (nur Creator+)'}</span>
            <span className="streamer-account-meta-hint">
              {account.creator_plus
                ? 'Nur während der Live-Button aktiv ist und du wirklich live bist.'
                : 'Belohnung nur mit Creator+ Rolle.'}
            </span>
          </div>
          <div className="streamer-account-meta-item">
            <span className="muted">Status</span>
            <span className={`streamer-account-status ${platformLive ? 'live' : 'offline'}`}>
              {platformLive ? (manualLive ? 'Live & angezeigt' : 'Live (nicht markiert)') : 'Offline'}
            </span>
          </div>
        </div>

        <div className="streamer-account-live">
          <button
            type="button"
            onClick={handleLiveToggle}
            disabled={liveSaving}
            className={`btn streamer-account-live-btn ${manualLive ? 'active' : ''}`}
          >
            {liveSaving ? 'Speichert...' : manualLive ? 'LIVE BEENDEN' : 'JETZT LIVE GEHEN'}
          </button>
          <p className="streamer-account-live-hint">
            {manualLive
              ? platformLive
                ? 'Du bist als Live markiert und wirst auf der Seite „Live Streams“ angezeigt.'
                : 'Markiert – starte jetzt deinen Stream, damit du in den Live Streams auftauchst.'
              : 'Du wirst erst in den Live Streams angezeigt, wenn du diesen Button drückst und wirklich live bist.'}
            <span className="streamer-account-live-hint-note">
              Der Button schaltet sich automatisch aus, sobald du offline gehst.
            </span>
          </p>
        </div>

        {account.creator_plus && (
          <form onSubmit={handleRedeem} className="streamer-account-redeem">
            <div className="streamer-account-redeem-row">
              <input
                type="number"
                min="1"
                max={wallet.coins}
                value={redeemAmount}
                onChange={(e) => setRedeemAmount(e.target.value)}
                placeholder="Anzahl Coins"
                className="streamer-account-input"
              />
              <button
                type="submit"
                disabled={redeeming || !redeemAmount || parseInt(redeemAmount || '0', 10) <= 0 || parseInt(redeemAmount || '0', 10) > wallet.coins}
                className="btn btn-primary"
              >
                {redeeming ? 'Löse ein...' : 'Coins einlösen'}
              </button>
            </div>
            <p className="muted">Löse deine Coins hier ein. Maximal {wallet.coins} Coins verfügbar.</p>
          </form>
        )}

        {error && <p className="streamer-account-error">{error}</p>}
        {success && <p className="streamer-account-success">{success}</p>}
      </div>
    </div>
  );
}
